// Drag-and-drop file picker used by the new-project wizard + the per-episode
// setup route. Keeps state local: the parent wires up the actual presigned-PUT
// flow on `onFileSelected`. Renders a dropzone with the file's name + a
// progress bar after upload starts. Validates client-side via `accept`.

import { Button, ProgressBar } from '@ohear/ui'
import type { DragEvent, ReactNode } from 'react'
import { useCallback, useId, useRef, useState } from 'react'

export interface UploadZoneProps {
  label: string
  /** MIME types or extension globs (`video/*`, `.srt`). */
  accept: string
  /** Bytes; rejects with a friendly message if exceeded. */
  maxBytes: number
  /** Human "what is this file" hint shown beneath the label. */
  hint: ReactNode
  required?: boolean
  /** Set externally once upload completes — drives the checkmark. */
  done?: boolean
  /** 0..1 progress. Null when idle. */
  progress?: number | null
  /** Friendly error message to display below the dropzone. */
  error?: string | null
  /** Currently selected (or in-flight) file's metadata. */
  file?: { name: string; size: number } | null
  onFileSelected: (file: File) => void
}

export function UploadZone({
  label,
  accept,
  maxBytes,
  hint,
  required,
  done,
  progress,
  error,
  file,
  onFileSelected,
}: UploadZoneProps) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [hovered, setHovered] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const validate = useCallback(
    (f: File): string | null => {
      if (f.size > maxBytes) {
        return `File is ${formatBytes(f.size)} but the limit is ${formatBytes(maxBytes)}.`
      }
      // `accept` validation is a smoke check; the server is the source of truth.
      if (accept && accept !== '*') {
        const lower = f.name.toLowerCase()
        const acceptParts = accept.split(',').map((s) => s.trim().toLowerCase())
        const ok = acceptParts.some((part) => {
          if (part.startsWith('.')) return lower.endsWith(part)
          if (part.endsWith('/*')) return f.type.startsWith(part.slice(0, -1))
          return f.type === part
        })
        if (!ok) return `Unexpected file type. Accepts: ${accept}.`
      }
      return null
    },
    [accept, maxBytes],
  )

  const handleFile = useCallback(
    (f: File | null | undefined) => {
      if (!f) return
      const err = validate(f)
      setLocalError(err)
      if (!err) onFileSelected(f)
    },
    [onFileSelected, validate],
  )

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setHovered(false)
    handleFile(e.dataTransfer.files?.[0])
  }

  const displayError = error ?? localError
  const pct = progress != null ? Math.round(progress * 100) : null
  const filenameDisplay = file?.name ?? 'No file selected yet'

  return (
    <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-2)] p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <label htmlFor={inputId} className="text-[14px] font-medium text-[var(--text)]">
          {label}
          {required ? <span className="text-[var(--red)]"> *</span> : null}
        </label>
        {done ? (
          <span className="text-[12px] font-medium text-[var(--green)]" aria-live="polite">
            Uploaded
          </span>
        ) : null}
      </div>

      <p className="mb-3 text-[13px] text-[var(--text-2)]">{hint}</p>

      <div
        role="button"
        tabIndex={0}
        aria-describedby={`${inputId}-status`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setHovered(true)
        }}
        onDragLeave={() => setHovered(false)}
        onDrop={onDrop}
        className={[
          'flex cursor-pointer items-center justify-between gap-3 rounded-[var(--radius)] border-2 border-dashed px-3 py-3 transition-colors',
          'focus-visible:shadow-[var(--focus-ring)] outline-none',
          hovered
            ? 'border-[var(--accent)] bg-[var(--bg-3)]'
            : 'border-[var(--border)] bg-[var(--bg)]',
        ].join(' ')}
      >
        <div className="min-w-0 flex-1 text-[13px] text-[var(--text-2)]">
          <div className="truncate" title={filenameDisplay}>
            {file ? file.name : 'Drop a file here or click to browse'}
          </div>
          {file ? (
            <div className="text-[11px] text-[var(--text-3)]">{formatBytes(file.size)}</div>
          ) : null}
        </div>
        <Button size="sm" variant="ghost" type="button">
          Browse
        </Button>
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
      />

      {pct != null ? (
        <div className="mt-3">
          <ProgressBar value={pct} label={`${label} upload`} size="sm" />
          <div
            id={`${inputId}-status`}
            aria-live="polite"
            className="mt-1 text-[11px] text-[var(--text-3)]"
          >
            {pct}% uploaded
          </div>
        </div>
      ) : null}

      {displayError ? (
        <p role="alert" className="mt-2 text-[12px] text-[var(--amber)]">
          {displayError}
        </p>
      ) : null}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let n = bytes / 1024
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`
}
